import React, {
  forwardRef, useImperativeHandle,
} from 'react';
import { useRemirrorContext, ReactFrameworkOutput } from '@remirror/react';
import { Extension } from '@remirror/core';

import styled from 'styled-components';
import Color from 'color';

// See: https://docs.remirror.org/guides/advanced-guide/
const InnerEditorWrapper = styled.div`
  .remirror-editor {
    box-sizing: border-box;
    position: relative;
    line-height: 1.6em;
    width: 100%;
    min-height: ${({ readOnly }) => (readOnly ? '142px' : '25px')} !important;
    word-break: normal;
    overflow: hidden !important;
    overflow-wrap: break-word;

    &:focus {
      outline: none;
    }

    &.ProseMirror-focused {
      .ProseMirror-menubar {
        display: none;
      }
    }

    &[contenteditable=false] {
      box-shadow: 0px 0px 0px 0 !important;
      padding: 0;
    }

    &[contenteditable=true] {
      padding: 10px;
      border-radius: 5px;
      background-color: #fff;
      box-shadow: 0 0 0 ${({ noShadow }) => (noShadow ? 0 : 1)}px rgba(0,0,0,0.1) !important;
      caret-color: ${({ theme }) => theme.primaryColour};

      @media (min-width: 768px) {
        .resizable:hover {
          box-shadow: rgba(0, 0, 0, 0.2) 0px 0px 10px;
          resize: both;

          &.resizable::after {
            content: '';
            width: 0px;
            height: 0px;
            position: absolute;
            border-top: 10px solid ${({ theme }) => theme.primaryColour};
            border-left: 10px solid transparent;
            border-right: 10px solid transparent;
            bottom: 0;
            transform: rotate(-45deg);
            right: -5px;
            pointer-events: none;
          }

          ::-webkit-resizer {
            display: none;
          }
        }
      }
    }

    &[contenteditable=false] {
      ul, ol {
        text-align: left;
      }
    }

    img {
      max-width: 100%;
    }

    h1 {
      font-size: 1.5rem;
    }

    h2 {
      font-size: 1.25rem;
    }

    p {
      margin: 0;
      letter-spacing: ${({ readOnly }) => (readOnly ? '0.6px' : '0px')};
      color: black;
    }

    p em {
      letter-spacing: 1.2px;
    }

    a {
      text-decoration: none !important;
      color: ${(props) => props.theme.primaryColour};
    }

    h1,h2,h3,h4,h5,p,a,span {
      font-family: 'Inter';
    }

    .remirror-is-empty{
      font-size: small !important;
      font-weight: normal !important;
    }

    .ProseMirror-selectednode {
      background-color: rgb(245, 248, 250);
    }

    .mention-suggest {
      pointer-events: none;
      cursor: default;
      color: black;
    }

    .mention, .mention-suggest {
      color: ${(props) => props.theme.primaryColour};
    }

    s > .selection {
      text-decoration: line-through;
    }

    span[data-ext=file] {
      cursor: pointer;
      background-color: ${({ theme }) => Color(theme.primaryColour).lighten(1.2).hex()};
      padding: 2px 4px;
      padding-right: 6px;
      border-radius: 2px;
    }

    span[data-node] {
      max-width: 98%;
      position: relative;
      min-width: 20px;
      min-height: 20px;
      display: inline-block;
      overflow: hidden;
      vertical-align: middle;

      &.mouseDown {
        > * {
          pointer-events: none;
        }
      }
      > * {
        object-fit: contain;
        width: 100%;
        height: 100%;
        display: inline-block;
      }
    }
  }
`;

type IReactTextEditor = {
  noShadow?: boolean,
  style?: object,
  readOnly?: boolean,
}

const ReactTextEditor = ({
  noShadow, style, readOnly: _readOnly,
}, ref: any) => {
  const context = useRemirrorContext();
  const { getRootProps } = context;
  useImperativeHandle(ref, () => context);

  return <InnerEditorWrapper noShadow={noShadow} style={style} {...getRootProps()} />;
};

const TextEditor = forwardRef<ReactFrameworkOutput<Extension>, IReactTextEditor>(ReactTextEditor);

export default TextEditor;
